import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, InputAdornment, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useCustomers } from '../api/hooks';
import { CustomerFormDialog } from '../components/forms/CustomerFormDialog';
import { EmptyState, ErrorState, LoadingState, UserText } from '../components/common/Common';
import { formatDateTime } from '../components/common/format';

export function CustomersPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  // Two extra columns would push the table into horizontal scroll on a tablet, so they drop out.
  const isCompactTable = useMediaQuery(theme.breakpoints.down('md'));

  const [term, setTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, isLoading, error, refetch } = useCustomers(term);

  const rows = data?.items ?? [];

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Typography variant="h1">{t('customers.title')}</Typography>
        <Stack direction="row" spacing={1.5}>
          <TextField
            size="small"
            placeholder={t('common.filterByName')}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            inputProps={{ dir: 'auto', 'data-testid': 'customer-filter' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            data-testid="new-customer"
          >
            {t('customers.newCustomer')}
          </Button>
        </Stack>
      </Stack>

      {isLoading && <LoadingState label={t('customers.loading')} />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {!isLoading && !error && rows.length === 0 && (
        <Paper variant="outlined" sx={{ borderRadius: '10px' }}>
          <EmptyState
            title={term ? t('customers.noMatch') : t('customers.empty')}
            description={term ? t('customers.noMatchDescription') : t('customers.emptyDescription')}
            action={
              !term && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
                  {t('customers.newCustomer')}
                </Button>
              )
            }
          />
        </Paper>
      )}

      {rows.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px', overflowX: 'auto' }}>
          <Table data-testid="customers-table">
            <TableHead>
              <TableRow>
                <TableCell>{t('customers.columns.name')}</TableCell>
                <TableCell>{t('customers.columns.country')}</TableCell>
                {!isCompactTable && <TableCell>{t('customers.columns.emailDomain')}</TableCell>}
                <TableCell>{t('customers.columns.partner')}</TableCell>
                {!isCompactTable && <TableCell>{t('customers.columns.owner')}</TableCell>}
                <TableCell align="right">{t('customers.columns.openOpportunities')}</TableCell>
                <TableCell>{t('customers.columns.lastUpdated')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((c) => (
                <TableRow
                  key={c.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/customers/${c.id}`)}
                  data-testid={`customer-row-${c.id}`}
                >
                  <TableCell><UserText>{c.name}</UserText></TableCell>
                  <TableCell><UserText>{c.country ?? '—'}</UserText></TableCell>
                  {!isCompactTable && <TableCell>{c.emailDomain ?? '—'}</TableCell>}
                  <TableCell><UserText>{c.partnerName ?? '—'}</UserText></TableCell>
                  {!isCompactTable && <TableCell><UserText>{c.ownerName ?? '—'}</UserText></TableCell>}
                  <TableCell align="right">
                    {c.openOpportunityCount > 0
                      ? <Chip size="small" label={c.openOpportunityCount} color="primary" variant="outlined" />
                      : <Typography variant="body2" color="text.disabled">0</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDateTime(c.updatedAtUtc)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CustomerFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={(c) => navigate(`/customers/${c.id}`)}
      />
    </Box>
  );
}
